import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

interface LayoutProps {
  children: React.ReactNode;
  onLogout: () => void;
}

const MENU_ITEMS = [
  { path: '/', label: 'Dashboard', icon: 'fa-solid fa-chart-pie' },
  { path: '/charts', label: 'Charts', icon: 'fa-solid fa-chart-line' },
  { type: 'divider', label: 'Assets' },
  { path: '/stocks', label: 'Aktien', icon: 'fa-solid fa-building-columns', badge: 'stock' },
  { path: '/bonds', label: 'Anleihen', icon: 'fa-solid fa-file-contract', badge: 'bond' },
  { path: '/indices', label: 'Indizes', icon: 'fa-solid fa-chart-column', badge: 'index' },
  { path: '/commodities', label: 'Rohstoffe', icon: 'fa-solid fa-industry', badge: 'commodity' },
  { path: '/crypto', label: 'Kryptowährungen', icon: 'fa-solid fa-coins', badge: 'crypto' },
  { path: '/forex', label: 'Devisen', icon: 'fa-solid fa-money-bill-transfer', badge: 'forex' },
  { type: 'divider', label: 'System' },
  { path: '/sources', label: 'API Quellen', icon: 'fa-solid fa-plug' },
  { path: '/rates', label: 'Wechselkurse', icon: 'fa-solid fa-euro-sign' },
  { path: '/consumers', label: 'API-Verbraucher', icon: 'fa-solid fa-key' },
  { path: '/news-sources', label: 'News Quellen', icon: 'fa-solid fa-rss' },
];

export default function Layout({ children, onLogout }: LayoutProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);

  return (
    <div style={{ display: 'flex', minHeight: '100vh' }}>
      {/* Sidebar */}
      <aside style={{
        width: collapsed ? '60px' : '220px',
        backgroundColor: '#0F1219',
        borderRight: '1px solid #1E2330',
        display: 'flex',
        flexDirection: 'column',
        transition: 'width 0.2s',
        flexShrink: 0,
        position: 'sticky',
        top: 0,
        height: '100vh',
        overflowY: 'auto',
      }}>
        {/* Logo */}
        <div
          style={{
            padding: collapsed ? '16px 12px' : '16px 20px',
            borderBottom: '1px solid #1E2330',
            cursor: 'pointer',
          }}
          onClick={() => setCollapsed(!collapsed)}
        >
          {collapsed ? (
            <span style={{ fontSize: '1.2rem', display: 'block', textAlign: 'center', color: '#00D97E' }}>
              <i className="fa-solid fa-chart-pie"></i>
            </span>
          ) : (
            <>
              <span style={{ fontSize: '1rem', fontWeight: 800 }}>
                <span style={{ color: '#00D97E' }}>MDS</span> Admin
              </span>
              <div style={{ fontSize: '0.6rem', color: '#4A5068', marginTop: '2px' }}>Market Data Service</div>
            </>
          )}
        </div>

        {/* Menu */}
        <nav style={{ flex: 1, padding: '8px', overflowY: 'auto' }}>
          {MENU_ITEMS.map((item, i) => {
            if (item.type === 'divider') {
              if (collapsed) return <div key={i} style={{ height: '1px', backgroundColor: '#1E2330', margin: '8px 4px' }} />;
              return (
                <div key={i} style={{
                  fontSize: '0.6rem',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '0.1em',
                  color: '#4A5068',
                  padding: '12px 12px 4px',
                }}>
                  {item.label}
                </div>
              );
            }

            const isActive = location.pathname === item.path;

            return (
              <button
                key={item.path}
                onClick={() => navigate(item.path!)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  width: '100%',
                  padding: collapsed ? '10px 0' : '8px 12px',
                  justifyContent: collapsed ? 'center' : 'flex-start',
                  border: 'none',
                  borderRadius: '8px',
                  backgroundColor: isActive ? '#1E2330' : 'transparent',
                  color: isActive ? '#E8EAED' : '#7B8399',
                  fontSize: '0.8rem',
                  fontWeight: isActive ? 600 : 400,
                  cursor: 'pointer',
                  transition: 'all 0.15s',
                  marginBottom: '2px',
                }}
                onMouseEnter={e => { if (!isActive) (e.target as HTMLButtonElement).style.backgroundColor = '#151921'; }}
                onMouseLeave={e => { if (!isActive) (e.target as HTMLButtonElement).style.backgroundColor = 'transparent'; }}
              >
                <span style={{ fontSize: '0.9rem', width: '16px', textAlign: 'center' }}>
                  <i className={item.icon}></i>
                </span>
                {!collapsed && <span>{item.label}</span>}
              </button>
            );
          })}
        </nav>

        {/* Logout */}
        <div style={{ padding: '8px', borderTop: '1px solid #1E2330' }}>
          <button
            onClick={onLogout}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              width: '100%',
              padding: collapsed ? '10px 0' : '8px 12px',
              justifyContent: collapsed ? 'center' : 'flex-start',
              border: 'none',
              borderRadius: '8px',
              backgroundColor: 'transparent',
              color: '#7B8399',
              fontSize: '0.8rem',
              cursor: 'pointer',
            }}
            onMouseEnter={e => (e.target as HTMLButtonElement).style.color = '#FF4757'}
            onMouseLeave={e => (e.target as HTMLButtonElement).style.color = '#7B8399'}
          >
            <span style={{ fontSize: '0.9rem', width: '16px', textAlign: 'center' }}>
              <i className="fa-solid fa-right-from-bracket"></i>
            </span>
            {!collapsed && <span>Abmelden</span>}
          </button>
        </div>
      </aside>

      {/* Main */}
      <main style={{ flex: 1, padding: '24px 32px', overflowY: 'auto' }}>
        {children}
      </main>
    </div>
  );
}
